/* ==========================================================================
   SPLASH Duel — interface-compatibility guard.

   CONFIG.duelRealm is a pkgPath, not a promise that whatever is deployed at
   that path still speaks the ABI this front end sends. On 2026-09-26 a front
   end and its contract disagreed this same way for a different realm and a
   real visitor's call came back 405, twice, with a comment sitting nearby
   that nobody read in time. A comment cannot stop a transaction; this can.

   So before this page will post or take anything, it asks the chain itself
   -- via reads.hasFunction, the same vm/qfuncs query reads.isDeployed
   already runs, not a version flag anybody could forget to bump -- whether
   CONFIG.duelRealm currently exports RecentResults.

   WHY RecentResults, AND WHY THIS COMMENT NO LONGER SAYS RevealNonce. The
   duel was redesigned on 2026-10-08: there is no secret, no nonce, no
   commit/reveal and so no RevealNonce at all any more -- that function does
   not exist on any contract, old or new, so checking for it would prove the
   OPPOSITE of compatibility: every realm, forever, would fail this check.
   RecentResults is new to v3 specifically (the old plaintext-nonce duel
   never had it, and neither did the sealed commit-reveal one this guard used
   to pin against), so its presence still means exactly what this guard
   exists to mean -- "the realm at this path speaks the ABI this page is
   about to send" -- for the contract actually deployed today. The day the
   next redesign ships, this constant is the one line that moves again.

   "Absent" and "could not find out" are kept as two different answers on
   purpose. Calling an unreachable RPC "the wrong contract" would let a real
   incompatibility hide behind the next retry; calling the wrong contract "a
   network blip" would let a real incompatibility get waved off as one. Both
   refuse -- an unknown contract is treated as an incompatible one -- but
   they say different things, so whoever reads the banner knows which to
   chase.
   ========================================================================== */

export const REQUIRED_FUNC = 'RecentResults';

function incompatible(pkgPath) {
  return {
    ok: false,
    reason: 'incompatible',
    message: `${pkgPath} is an older version of the duel contract: it does not export ${REQUIRED_FUNC} and cannot run the flow this page sends. Posting and taking are disabled here until a matching contract is deployed at ${pkgPath}.`,
  };
}

function unknown(pkgPath, e) {
  const detail = e?.message ?? String(e);
  return {
    ok: false,
    reason: 'unknown',
    message: `Could not check whether ${pkgPath} supports the sealed reveal flow (${detail}). Posting and taking are disabled here until that can be confirmed — this is not the same as knowing it is the wrong contract, only that it has not been checked yet.`,
  };
}

// One of these per page load. `query` is called as query(pkgPath,
// REQUIRED_FUNC) and must resolve true/false exactly like reads.hasFunction
// does -- production code passes that in; tests pass a stub so the suite
// never makes a network call. The settled outcome (success OR failure
// alike) is memoized: a page with ten interactions still makes one query,
// and callers that both ask before it has settled share the one in-flight
// promise rather than firing a second.
export function createDuelCompat(pkgPath, query) {
  let pending = null;
  return {
    check() {
      if (!pending) {
        // query() is called SYNCHRONOUSLY, right here, on the first check()
        // -- not deferred a tick -- so that two callers racing before it
        // settles (the concurrent case memoization exists for) are already
        // looking at the same in-flight promise by the time either of them
        // returns control to the caller. A synchronous throw is caught just
        // as a rejection would be: an incompatible or unreachable realm
        // must refuse either way.
        let started;
        try { started = Promise.resolve(query(pkgPath, REQUIRED_FUNC)); }
        catch (e) { started = Promise.reject(e); }
        pending = started
          .then((has) => (has ? { ok: true } : incompatible(pkgPath)))
          .catch((e) => unknown(pkgPath, e));
      }
      return pending;
    },
  };
}
